import { useState } from "react";
import axios from "axios";
import { API_BASE, ASSETS_BASE } from "@config/env";
import Button from "@common/Button";
import { availColor, availBg, availLabel } from "./AvailabilityRing";
import SkillsModal from "./SkillsModal";
import { Modal, ModalFooter } from "@common/Modal";

const GlanceLabel = ({ children }) => <span className="fc-glance-label">{children}</span>;

// ── Shared modal for certs/languages ─────────────────────────────────────────
function ShowMoreModal({ title, icon, iconBg, iconColor, count, children, onClose }) {
  const modalTitle = (
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <div style={{ width:36, height:36, borderRadius:10, background:iconBg, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <i className={`fa-solid ${icon}`} style={{ color:iconColor, fontSize:15 }} />
      </div>
      <div>
        <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>{title}</div>
        <div style={{ fontSize:11, color:"var(--fc-text-muted)", marginTop:2 }}>{count} total</div>
      </div>
    </div>
  );
  return (
    <Modal title={modalTitle} onClose={onClose} maxWidth={420}
      footer={<ModalFooter onConfirm={onClose} confirmLabel="Close" />}>
      {children}
    </Modal>
  );
}


// ── Availability update modal ─────────────────────────────────────────────────
function AvailUpdateModal({ current, resourceSlug, userSlug, candidateName, onClose, onSaved }) {
  const [val,   setVal]   = useState(parseInt(current) || 0);
  const [saving,setSaving]= useState(false);
  const [error, setError] = useState("");

  const availColor = val >= 30 ? "#16A34A" : val >= 10 ? "#F59E0B" : "#DC2626";
  const availLabel = val >= 30 ? "Available" : val >= 10 ? "Partially Available" : "Busy";

  const save = async () => {
    setSaving(true); setError("");
    const fd = new FormData();
    fd.append("current_user_slug", userSlug);
    fd.append("resource_slug",     resourceSlug);
    fd.append("availability",      val);
    const res = await axios.post(API_BASE + "save-resource-availability", fd)
      .then(r => r.data).catch(() => null);
    setSaving(false);
    if (res && Number(res.status) === 1) {
      onSaved(val);
    } else {
      setError(res?.message || "Failed to save availability.");
    }
  };

  const title = (
    <div style={{ display:"flex", alignItems:"center", gap:12 }}>
      <div style={{ width:36, height:36, borderRadius:10, background:"var(--fc-primary-light)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
        <i className="fa-solid fa-clock" style={{ color:"#2563EB", fontSize:14 }} />
      </div>
      <div>
        <div style={{ fontSize:15, fontWeight:700, color:"var(--fc-text-primary)" }}>Update Availability</div>
        <div style={{ fontSize:12, color:"var(--fc-text-muted)" }}>{candidateName}</div>
      </div>
    </div>
  );

  return (
    <Modal title={title} onClose={onClose} maxWidth={400}
      footer={
        <ModalFooter onCancel={onClose} onConfirm={save} confirmBusy={saving}
          confirmLabel={saving ? "Saving..." : "Save"} />
      }>
      <div style={{ textAlign:"center", marginBottom:24 }}>
        <div style={{ fontSize:56, fontWeight:900, color:availColor, lineHeight:1 }}>{val}</div>
        <div style={{ fontSize:14, fontWeight:600, color:availColor, marginTop:6 }}>hrs/week — {availLabel}</div>
      </div>

      <input type="range" min={0} max={40} value={val}
        onChange={e => setVal(parseInt(e.target.value))}
        style={{ width:"100%", accentColor:availColor }} />
      <div style={{ display:"flex", justifyContent:"space-between", fontSize:11, color:"var(--fc-text-subtle)", marginTop:4 }}>
        <span>0 — Busy</span><span>20 hrs</span><span>40 — Available</span>
      </div>

      <div style={{ display:"flex", gap:8, justifyContent:"center", marginTop:18, flexWrap:"wrap" }}>
        {[0, 10, 20, 30, 40].map(v => (
          <button key={v} onClick={() => setVal(v)}
            style={{ padding:"5px 14px", borderRadius:99, border:`1.5px solid ${val===v ? availColor : "var(--fc-border)"}`, background:val===v ? availColor+"18" : "var(--fc-card-bg)", color:val===v ? availColor : "var(--fc-text-muted)", fontSize:12, fontWeight:600, cursor:"pointer", fontFamily:"inherit", transition:"all 0.15s" }}>
            {v} hrs
          </button>
        ))}
      </div>

      {error && <div style={{ fontSize:12, color:"var(--fc-red)", textAlign:"center", marginTop:12 }}>{error}</div>}
    </Modal>
  );
}

export default function ProfileGlance({ candidate, userSlug, readOnly = true, hideIdentity = false }) {
  const [showSkills, setShowSkills] = useState(false);
  const [showLangs,  setShowLangs]  = useState(false);
  const [showCerts,  setShowCerts]  = useState(false);
  const [showAvail,  setShowAvail]  = useState(false);
  const [localAvail, setLocalAvail] = useState(candidate.availability);

  const avail  = localAvail ?? candidate.availability;
  const color  = availColor(avail);
  const bg     = availBg(avail);
  const label  = availLabel(avail);
  const r      = 18;
  const circ   = 2 * Math.PI * r;
  const filled = (Math.min(avail, 40) / 40) * circ;

  // Use real skillCount from API if available
  const skillCount  = candidate.skillCount || candidate.skills.length;
  const topSkills   = candidate.skills.slice(0, 4);
  const extraSkills = skillCount - topSkills.length;

  const certs      = candidate.certifications || [];
  const topCerts   = certs.slice(0, 2);
  const extraCerts = certs.length - topCerts.length;

  const languages  = candidate.languages || [];
  const topLangs   = languages.slice(0, 2);
  const extraLangs = languages.length - topLangs.length;

  return (
    <>
      <div className="fc-glance-grid">

        {/* Skills */}
        <div className="fc-glance-cell">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <GlanceLabel>Skills</GlanceLabel>
            <span className="fc-tag" style={{ fontSize:10, padding:"1px 7px" }}>{skillCount}</span>
          </div>
          <div className="d-flex flex-wrap gap-1">
            {topSkills.map(s => <span key={s} className="fc-skill">{s}</span>)}
            {extraSkills > 0 && (
              <button onClick={()=>setShowSkills(true)}
                className="fc-tag border-0" style={{ cursor:"pointer", fontSize:11, background:"var(--fc-primary-light)", color:"#2563EB" }}>
                +{extraSkills} more
              </button>
            )}
            {skillCount === 0 && <span style={{ fontSize:12, color:"var(--fc-text-hint)", fontStyle:"italic" }}>None</span>}
          </div>
        </div>

        {/* Certifications */}
        <div className="fc-glance-cell">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <GlanceLabel>Certifications</GlanceLabel>
            {certs.length > 0 && <span className="fc-tag" style={{ fontSize:10, padding:"1px 7px" }}>{certs.length}</span>}
          </div>
          {certs.length === 0
            ? <span style={{ fontSize:12, color:"var(--fc-text-hint)", fontStyle:"italic" }}>None</span>
            : (
              <>
                {topCerts.map((c, i) => (
                  <div key={i} className="d-flex align-items-center gap-2 mb-1">
                    <i className="fa-solid fa-certificate" style={{ color:"var(--fc-amber)", fontSize:11, flexShrink:0 }} />
                    <span style={{ fontSize:11, fontWeight:600, color:"var(--fc-text-secondary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                      {(c.title||"").length > 24 ? (c.title||"").slice(0, 24)+"…" : c.title}
                    </span>
                  </div>
                ))}
                {extraCerts > 0 && (
                  <button onClick={()=>setShowCerts(true)}
                    style={{ background:"none", border:"none", cursor:"pointer", fontSize:11, color:"var(--fc-blue)", fontWeight:600, padding:"2px 0", fontFamily:"inherit" }}>
                    +{extraCerts} more
                  </button>
                )}
              </>
            )
          }
        </div>

        {/* Languages */}
        <div className="fc-glance-cell">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <GlanceLabel>Languages</GlanceLabel>
            {languages.length > 0 && <span className="fc-tag" style={{ fontSize:10, padding:"1px 7px" }}>{languages.length}</span>}
          </div>
          {languages.length === 0
            ? <span style={{ fontSize:12, color:"var(--fc-text-hint)", fontStyle:"italic" }}>None</span>
            : (
              <div className="d-flex flex-wrap gap-1">
                {topLangs.map(l => <span key={l} className="fc-tag fc-tag-purple">{l}</span>)}
                {extraLangs > 0 && (
                  <button onClick={()=>setShowLangs(true)}
                    className="fc-tag fc-tag-purple border-0" style={{ cursor:"pointer", fontSize:11 }}>
                    +{extraLangs} more
                  </button>
                )}
              </div>
            )
          }
        </div>

        {/* Availability */}
        <div className="fc-glance-cell">
          <div className="d-flex align-items-center justify-content-between mb-2">
            <GlanceLabel>Availability</GlanceLabel>
            {!readOnly && (
              <button onClick={() => setShowAvail(true)}
                style={{ background:"none", border:"none", cursor:"pointer", color:"var(--fc-blue)", fontSize:11, padding:0, fontFamily:"inherit", fontWeight:600 }}>
                <i className="fa-solid fa-pen" style={{fontSize:9, marginRight:3}} />Edit
              </button>
            )}
          </div>
          <div className="d-flex align-items-center gap-2"
            onClick={() => !readOnly && setShowAvail(true)}
            style={{ cursor: readOnly ? "default" : "pointer" }}>
            <div className="position-relative flex-shrink-0" style={{ width:44, height:44 }}>
              <svg width={44} height={44} style={{ transform:"rotate(-90deg)" }}>
                <circle cx={22} cy={22} r={r} fill="none" stroke="var(--fc-border-light)" strokeWidth={5} />
                <circle cx={22} cy={22} r={r} fill="none" stroke={color} strokeWidth={5}
                  strokeDasharray={`${filled} ${circ}`} strokeLinecap="round" />
              </svg>
              <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center">
                <span style={{ fontSize:10, fontWeight:800, color }}>{avail}</span>
              </div>
            </div>
            <div>
              <span className="badge rounded-pill fw-bold" style={{ fontSize:10, background:bg, color }}>{label}</span>
              <div style={{ fontSize:10, color:"var(--fc-text-subtle)", marginTop:3 }}>{avail} hrs/wk</div>
            </div>
          </div>
        </div>

        {/* Files — real documents from user_document table, only shown if any exist */}
        {candidate.documents && candidate.documents.length > 0 && (
          <div className="fc-glance-cell">
            <GlanceLabel>Files</GlanceLabel>
            <div className="d-flex flex-column gap-2">
              {candidate.documents.map((doc) => {
                const ext = (doc.document_title || "").split(".").pop().toLowerCase();
                const isPdf = ext === "pdf";
                const isDoc = ext === "doc" || ext === "docx";
                const color = isPdf ? "var(--fc-red)" : isDoc ? "var(--fc-primary)" : "var(--fc-text-muted)";
                const bg    = isPdf ? "var(--fc-red-light)" : isDoc ? "var(--fc-primary-light)" : "var(--fc-border-light)";
                const border= isPdf ? "var(--fc-red-border)" : isDoc ? "var(--fc-primary-border)" : "var(--fc-border)";
                const displayName = hideIdentity
                  ? `Document${isPdf ? ".pdf" : isDoc ? ".docx" : ""}`
                  : (doc.document_title || "").length > 40
                    ? doc.document_title.slice(0, 25) + "..." + doc.document_title.slice(-8)
                    : doc.document_title;
                return (
                  <a key={doc.document_id} href={`${ASSETS_BASE}uploads/resume/${doc.document_path}`} download target="_blank" rel="noopener noreferrer"
                    className="d-flex align-items-center gap-2 p-2 rounded-2 text-decoration-none"
                    style={{ background:bg, border:`1px solid ${border}`, cursor:"pointer" }}>
                    <i className={`fa-solid ${isPdf?"fa-file-pdf":isDoc?"fa-file-word":"fa-file"} flex-shrink-0`} style={{ color, fontSize:16 }} />
                    <div className="overflow-hidden flex-grow-1">
                      <div style={{ fontSize:11, fontWeight:700, color:"var(--fc-text-secondary)", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{displayName}</div>
                      <div style={{ fontSize:10, color:"var(--fc-text-subtle)" }}>Download</div>
                    </div>
                    <i className="fa-solid fa-download flex-shrink-0" style={{ fontSize:10, color:"var(--fc-text-subtle)" }} />
                  </a>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Skills modal */}
      {showSkills && <SkillsModal candidate={candidate} userSlug={userSlug} onClose={()=>setShowSkills(false)} />}

      {/* Certs modal */}
      {showCerts && (
        <ShowMoreModal title="Certifications" icon="fa-certificate" iconBg="var(--fc-amber-light)" iconColor="#D97706"
          count={certs.length} onClose={()=>setShowCerts(false)}>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {certs.map((c,i) => (
              <div key={i} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", background:"var(--fc-amber-light)", border:"1px solid var(--fc-amber-border)", borderRadius:10 }}>
                <i className="fa-solid fa-certificate" style={{ color:"#D97706", fontSize:14, flexShrink:0 }} />
                <span style={{ fontSize:13, fontWeight:600, color:"var(--fc-amber-text)" }}>{c.title}</span>
              </div>
            ))}
          </div>
        </ShowMoreModal>
      )}

      {/* Availability edit modal */}
      {showAvail && !readOnly && (
        <AvailUpdateModal
          current={avail}
          resourceSlug={candidate.user_slug || candidate.id}
          userSlug={userSlug}
          candidateName={hideIdentity ? "this candidate" : candidate.name}
          onClose={() => setShowAvail(false)}
          onSaved={(newVal) => {
            setLocalAvail(newVal);
            setShowAvail(false);
          }}
        />
      )}

      {/* Languages modal */}
      {showLangs && (
        <ShowMoreModal title="Languages" icon="fa-language" iconBg="var(--fc-green-light)" iconColor="#16A34A"
          count={languages.length} onClose={()=>setShowLangs(false)}>
          <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
            {languages.map(l => (
              <span key={l} style={{ padding:"6px 14px", background:"var(--fc-green-light)", border:"1px solid var(--fc-green-border)", borderRadius:99, fontSize:13, fontWeight:600, color:"var(--fc-green-text)" }}>{l}</span>
            ))}
          </div>
        </ShowMoreModal>
      )}
    </>
  );
}